import { Button, Card, Col, DatePicker, Form, Input, Row, Select, Typography } from 'antd';
import dayjs from 'dayjs';
import DmnModeler from 'dmn-js';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import BoldButtonLabel from '../../../../components/BoldButtonLabel';
import TenantInfo from '../../../../components/TenantIdInfo';
import _ from '../../../../helpers/lodash';
import { displayErrorNotifications } from '../../../../helpers/toast.helpers';
import { useTimezone } from '../../../../hooks/useTimezone';
import PrimaryLayout from '../../../../layouts/primary-layout';
import { useLoader } from '../../../../stores/use-loader';
import { loyaltyService } from '../../services/loyalty.service';

interface IBurnRulesViewProps {}

const BurnRuleView: React.FunctionComponent<IBurnRulesViewProps> = props => {
  const { t } = useTranslation();
  const [createForm] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const { timezoneSelectOptions } = useTimezone();

  const [burnRuleDetails, setBurnRuleDetails] = React.useState({} as any);
  const modellerRef = React.useRef<any>(null);

  const { setLoading } = useLoader(({ setLoading }) => ({ setLoading }));

  React.useEffect(() => {
    if (_.isEmpty(burnRuleDetails)) {
      fetchRuleDetails();
    }

    if (modellerRef.current === null && burnRuleDetails.xml) {
      initializeModeller();
    }

    return () => {
      if (modellerRef.current !== null) {
        modellerRef.current.detach();
        modellerRef.current = null;
      }
    };
  }, [burnRuleDetails]);

  const fetchRuleDetails = async () => {
    setLoading(true);
    const { data, errors } = await loyaltyService.getBurnRuleDetail(id as string);
    if (_.isEmpty(errors)) {
      setBurnRuleDetails(data);
      let datePresent = data.start_date && data.end_date;
      let formValues = {
        burn_template_type: data.burn_template_type,
        name: data.name,
        reward_name: data.reward_name,
        timezone: data.time_zone,
        date_range: datePresent ? [dayjs(data.start_date), dayjs(data.end_date)] : null
      };
      createForm.setFieldsValue(formValues);
    } else {
      displayErrorNotifications(errors);
    }
    setLoading(false);
  };

  const initializeModeller = async () => {
    const modeller = new DmnModeler({
      container: '#viewBurnRulesCanvas'
    });
    modellerRef.current = modeller;
    try {
      const { warnings } = await modeller.importXML(burnRuleDetails.xml);
      if (warnings.length) {
        console.log('import with warnings', warnings);
      } else {
        console.log('import successful');
      }
    } catch (err) {
      console.log('something went wrong:', err);
    }
  };

  return (
    <PrimaryLayout>
      <div className="container mx-auto px-4">
        <Card>
          <Row justify={'space-between'} className="mb-4">
            <Col>
              <Typography.Title level={3} className="text-[#2e2a5b]">
                {t('viewBurnRule')}
              </Typography.Title>
            </Col>
          </Row>
          <div className="flex gap-4">
            <TenantInfo />
          </div>
          <section className="mt-4">
            <Form layout="vertical" form={createForm}>
              <Row gutter={12}>
                <Col xs={24} md={6}>
                  <Form.Item name="burn_template_type" label={t('templateType')}>
                    <Select
                      placeholder={t('selectTemplateType')}
                      size="large"
                      disabled={true}
                      options={[
                        { label: t('customerOrder'), value: 'CUSTOMER_ORDER' },
                        { label: t('giftCard'), value: 'GIFT_CARD' },
                        { label: t('transferReward'), value: 'TRANSFER_REWARD' }
                      ]}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    rules={[
                      { message: t('thisFieldIsRequired'), required: true },
                      {
                        validator: async (_, ruleName) => {
                          if (/\s/.test(ruleName)) {
                            return Promise.reject(new Error(t('noSpacesAllowed')));
                          }
                        }
                      }
                    ]}
                    name="name"
                    label={t('ruleName')}
                  >
                    <Input placeholder={t('enterRuleName')} size="large" disabled={true} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item
                    rules={[{ message: t('thisFieldIsRequired'), required: true }]}
                    name="reward_name"
                    label={t('rewardName')}
                  >
                    <Input placeholder={t('enterRewardName')} size="large" disabled={true} />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col xs={24} md={6}>
                  <Form.Item
                    rules={[{ message: t('thisFieldIsRequired'), required: true }]}
                    name="timezone"
                    label={t('timezone')}
                  >
                    <Select
                      options={timezoneSelectOptions}
                      placeholder={t('selectTimezone')}
                      size="large"
                      disabled={true}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={6}>
                  <Form.Item name="date_range" label={t('date')}>
                    <DatePicker.RangePicker size="large" disabled={true} />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
            <div id="viewBurnRulesCanvas" className="h-[400px]"></div>
            <Row gutter={12} className="mt-4">
              <Col xs={24} md={8} lg={6}>
                <Button block size="large" onClick={() => navigate(`/loyalty/config/burn-rule`)}>
                  <BoldButtonLabel labelText={t('back')} />{' '}
                </Button>
              </Col>
            </Row>
          </section>
        </Card>
      </div>
    </PrimaryLayout>
  );
};

export default BurnRuleView;
